import React, { useEffect, useRef } from 'react';
import { select } from 'd3-selection';
import { scaleLinear } from 'd3-scale';
import '@d3fc/d3fc-element';
import { chartCartesian } from '@d3fc/d3fc-chart';
import { extentLinear } from '@d3fc/d3fc-extent';
import { LiquiditySummaryData } from '../liquiditySummaryData';
import {
    getChartData,
    getStackedChartData as getChartSeriesData,
    LiquiditySummaryChartSeriesPoint,
} from './liquiditySummaryChartData';
import { liquiditySummaryChartSeries } from './liquiditySummaryChartSeries';
import { renderXAxis } from './xAxis';
import { renderYAxis } from './yAxis';
import { BarMouseOver } from './BarMouseOver';
import './LiquiditySummaryChart.scss';
import { useWidgetContext } from 'widgets/WidgetContext';
import { LiquiditySummaryChartHeader } from 'widgets/liquiditySummary/chart/LiquiditySummaryChartHeader';
import { useLiquiditySummaryChartDimensions } from 'widgets/liquiditySummary/chart/dimensions';

/* eslint-disable react-hooks/exhaustive-deps */

export interface LiquiditySummaryChartProps {
    symbol: string;
    data?: LiquiditySummaryData;
    haveData: boolean;
}

export default function LiquiditySummaryChart(
    props: LiquiditySummaryChartProps
) {
    const { widgetKey, width } = useWidgetContext();
    const { data, symbol, haveData } = props;
    const nodeRef = useRef<HTMLDivElement>(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const chartRef = useRef<any>();

    const xScaleRef = useRef(scaleLinear());
    const yScaleRef = useRef(scaleLinear());

    const mouseOverRef = useRef(new BarMouseOver());

    const chartData = getChartData(data);
    const chartSeriesData = getChartSeriesData(chartData);

    const chartSeries = liquiditySummaryChartSeries(
        chartSeriesData.series,
        mouseOverRef.current,
        yScaleRef.current
    );

    const xExtent = extentLinear()
        .accessors([
            (a: LiquiditySummaryChartSeriesPoint[]) => {
                return a.map((d: LiquiditySummaryChartSeriesPoint) => {
                    return d[1];
                });
            },
        ])
        .pad([0, 0.1]);

    const yExtent = extentLinear()
        .accessors([
            (a: LiquiditySummaryChartSeriesPoint[]) => {
                return a.map((d: LiquiditySummaryChartSeriesPoint) => {
                    return d.data.row;
                });
            },
        ])
        .pad([1.3, 0.2])
        .padUnit('domain');

    useEffect(() => {
        chartRef.current = chartCartesian(xScaleRef.current, yScaleRef.current)
            .yOrient('left')
            .xTickSizeOuter(0)
            .yTickSizeOuter(0);
    }, [widgetKey]);

    const { chartHeight, chartContainerHeight } =
        useLiquiditySummaryChartDimensions();

    useEffect(() => {
        const node = nodeRef.current!;
        const chart = chartRef.current!;

        if (!data) {
            xScaleRef.current.domain([0, 10000]);
        } else {
            xScaleRef.current.domain(xExtent(chartSeriesData.series));
        }
        yScaleRef.current.domain(yExtent(chartSeriesData.series));

        chart.svgPlotArea(chartSeries);

        select(node).datum(chartSeriesData.series).call(chart);

        renderXAxis(chartRef.current, chartSeriesData.maxX);
        renderYAxis(chartRef.current);
    }, [data, chartHeight, width]);

    return (
        <div className="LiquiditySummaryChart">
            <LiquiditySummaryChartHeader
                haveData={haveData}
                chartData={chartData}
                isDefault={!data}
                mouseOver={mouseOverRef.current}
                symbol={symbol}
            />
            <div
                className="chart-container"
                style={{
                    height: chartContainerHeight + 'px',
                }}
            >
                <div
                    ref={nodeRef}
                    className="Chart"
                    style={{
                        height: chartHeight + 'px',
                    }}
                />
            </div>
        </div>
    );
}
